import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { fetchExitGrid, saveExitGrid } from '../../services/terminal/terminalApi';

const LS_KEY = 'ts_exit_grid';
/** Survives refresh AND new tabs; cleared on logout. Matched to the JWT's
 *  `iat` so a fresh login (new token) re-opens the gate, but every tab opened
 *  under one login shares the confirmation. Deliberately localStorage, not
 *  sessionStorage: sessionStorage is per-tab, so a second tab would re-gate a
 *  grid the user had already set this login. */
const CONFIRM_KEY = 'ts_exit_grid_confirmed';

/** The grid's discrete options (mockup TP_LEVELS / RATIOS). Shared so the
 *  home popover and the stock ticket can't drift apart.
 *  Mirrors API_TP_LEVELS in the backend's utils/tp_sl_constants.py — a level
 *  here that the backend does not store returns an all-zero grid. */
export const TP_LEVELS = [0.5, 1, 2, 3, 5, 10, 15];
export const RATIOS = [1, 2, 3, 4];

function readCache() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v && typeof v.tp === 'number' && typeof v.ratio === 'number' ? v : null;
  } catch {
    return null;
  }
}

function decodeTokenClaims() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return { userId: null, iat: null };
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      userId: payload.sub || localStorage.getItem('userId'),
      iat: payload.iat ?? null,
    };
  } catch {
    return { userId: null, iat: null };
  }
}

function readSessionConfirmed() {
  try {
    const raw = localStorage.getItem(CONFIRM_KEY) || sessionStorage.getItem(CONFIRM_KEY);
    if (!raw) return false;
    const stored = JSON.parse(raw);
    const { userId, iat } = decodeTokenClaims();
    if (userId != null && iat != null) {
      return stored.userId === String(userId) && stored.iat === iat;
    }
    // Anonymous latches stay tab-scoped: with no token there is no login to
    // key on, so honouring a persisted one would never re-gate.
    return sessionStorage.getItem(CONFIRM_KEY) != null && stored.anonymous === true;
  } catch {
    return false;
  }
}

function writeSessionConfirmed() {
  const { userId, iat } = decodeTokenClaims();
  if (userId != null && iat != null) {
    localStorage.setItem(CONFIRM_KEY, JSON.stringify({ userId: String(userId), iat }));
  } else {
    sessionStorage.setItem(CONFIRM_KEY, JSON.stringify({ anonymous: true }));
  }
}

/**
 * Exit-grid preference. Server + localStorage hold the user's last choice;
 * a login-scoped latch (`gridConfirmedThisLoad`) mirrors KAN-64's
 * `riskTermsChosen` — stored values hydrate silently but `grid` stays null
 * until the user explicitly applies again (every login, not every refresh and
 * not every tab). grid === null → gates stay up, nothing preselected in the
 * popover.
 */
// The grid is one shared value, not one per call site. Every consumer used to
// hold its own useState, so applying a new grid in the topbar updated only the
// topbar: the pages below kept their previous object, their effects saw
// unchanged deps (`[anchor, grid?.tp, grid?.ratio]`) and never refetched, and
// the win rate / expectancy / matrix on screen stayed at the old exits until a
// reload. The value lives here and components subscribe to it.
//
// `grid` is an object read straight into deps and passed as a prop, so the
// snapshot must be reference-stable — a fresh object per read would spin
// useSyncExternalStore and re-run every consumer's effect on each render.
let current = readCache();
let synced = false;
/** In-memory mirror of the confirmation latch — restored from storage on load
 *  so refresh and new tabs keep the grid, but a new JWT (login) does not. */
let gridConfirmedThisLoad = readSessionConfirmed();
const listeners = new Set();

function emit() {
  listeners.forEach((fn) => fn());
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const getGrid = () => (gridConfirmedThisLoad ? current : null);
const getSynced = () => synced;

/** Cross-tab: a grid applied (or a session cleared) in another tab should not
 *  leave this one stale. The latch is watched alongside the value so a tab
 *  already open when the user confirms elsewhere ungates too — and so logging
 *  out in one tab re-gates the others. */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === CONFIRM_KEY) {
      const next = readSessionConfirmed();
      if (next === gridConfirmedThisLoad) return;
      gridConfirmedThisLoad = next;
      emit();
      return;
    }
    if (e.key !== LS_KEY) return;
    const next = readCache();
    if (next?.tp === current?.tp && next?.ratio === current?.ratio) return;
    current = next;
    emit();
  });
}

// Several components call this hook on one page (the home ctxrow control and
// the topbar chip, for instance) and each effect would issue its own GET. One
// shared in-flight promise collapses them into a single request; it is cleared
// on save so the next mount re-reads the server.
let inFlight = null;
function loadGridOnce() {
  if (!inFlight) {
    inFlight = fetchExitGrid().finally(() => {
      // Hold the resolved promise for this tick only — long enough for sibling
      // mounts to share it, short enough that a later navigation refetches.
      setTimeout(() => { inFlight = null; }, 0);
    });
  }
  return inFlight;
}

export default function useExitGrid() {
  const grid = useSyncExternalStore(subscribe, getGrid, getGrid);
  const isSynced = useSyncExternalStore(subscribe, getSynced, getSynced);

  useEffect(() => {
    if (synced) return; // one server read per load, shared by every consumer
    // Resolution updates the store, not this component's state, so it must not
    // be cancelled on unmount: the mount that starts the read is often the
    // topbar, and bailing on its teardown would strand every other consumer
    // permanently unsynced.
    loadGridOnce()
      .then((server) => {
        if (synced) return; // a save landed first and supersedes the server read
        if (server.tp != null && server.ratio != null) {
          current = { tp: server.tp, ratio: server.ratio };
          localStorage.setItem(LS_KEY, JSON.stringify(current));
        } else if (readCache() == null) {
          current = null;
        }
        synced = true;
        emit();
      })
      .catch(() => {
        // offline/dev: cache (or null) stands
        if (synced) return;
        synced = true;
        emit();
      });
  }, []);

  const save = useCallback(async (tp, ratio) => {
    const next = { tp, ratio };
    gridConfirmedThisLoad = true;
    writeSessionConfirmed();
    current = next; // optimistic — the popover just closed on Apply
    // A save supersedes any GET still in flight, which would otherwise resolve
    // with the pre-save server value and overwrite the choice just made.
    inFlight = null;
    synced = true;
    localStorage.setItem(LS_KEY, JSON.stringify(next));
    emit();
    try {
      await saveExitGrid(tp, ratio);
    } catch {
      /* offline/dev: the local mirror keeps the choice; next sync reconciles */
    }
    return next;
  }, []);

  return { grid, synced: isSynced, save };
}

/** @internal Re-initializes in-memory state from storage (simulates page reload). */
export function rehydrateExitGridFromStorage() {
  current = readCache();
  synced = false;
  inFlight = null;
  gridConfirmedThisLoad = readSessionConfirmed();
  emit();
}

/** Clears the session latch (call on logout). */
export function clearExitGridSession() {
  try {
    localStorage.removeItem(CONFIRM_KEY);
    sessionStorage.removeItem(CONFIRM_KEY);
  } catch {
    /* ignore */
  }
  gridConfirmedThisLoad = false;
  emit();
}

/** @internal Resets the module store between tests. */
export function resetExitGridStore() {
  clearExitGridSession();
  current = readCache();
  synced = false;
  inFlight = null;
  emit();
}

/** @internal Marks a hydrated grid as confirmed for the current session. */
export function confirmExitGridThisLoad() {
  if (!current) return;
  gridConfirmedThisLoad = true;
  writeSessionConfirmed();
  emit();
}

function slStr(grid) {
  const sl = grid.tp / grid.ratio;
  return sl % 1 ? sl.toFixed(2) : String(sl);
}

/** Full form for the home ctxrow / gate copy. */
export function gridLabel(grid) {
  if (!grid) return null;
  return `TP ${grid.tp}% · SL ${slStr(grid)}% · ${grid.ratio}:1`;
}

/** Compact form for the topbar chip (mockup gridShort — no ratio suffix). */
export function gridShort(grid) {
  if (!grid) return null;
  return `TP ${grid.tp}% · SL ${slStr(grid)}%`;
}
